import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Search, MapPin, Clock, Phone, Star, Filter, Navigation } from 'lucide-react';
import InteractiveMap from '../components/InteractiveMap.tsx';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #000000 0%, #333333 100%);
  color: white;
  padding: 3rem 0;
  text-align: center;
  border-radius: 0 0 20px 20px;
  margin-bottom: 3rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const SearchSection = styled.section`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 3rem;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.div`
  flex: 1;
  position: relative;
  
  input {
    width: 100%;
    padding: 1rem 1rem 1rem 3rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #FFD700;
    }
  }
  
  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`;

const FilterButton = styled.button`
  background: #FFD700;
  color: #000000;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #FFED4E;
    transform: translateY(-2px);
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;

const FilterChip = styled.button<{ active: boolean }>`
  background: ${props => props.active ? '#FFD700' : '#f0f0f0'};
  color: ${props => props.active ? '#000000' : '#666'};
  border: 2px solid ${props => props.active ? '#FFD700' : '#e0e0e0'};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.active ? '#FFED4E' : '#e0e0e0'};
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const RestaurantsList = styled.section`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: #000000;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RestaurantCard = styled(motion.div)`
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #FFD700;
    box-shadow: 0 5px 20px rgba(255, 215, 0, 0.2);
    transform: translateY(-2px);
  }
`;

const RestaurantHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const RestaurantName = styled.h3`
  color: #000000;
  font-size: 1.3rem;
  margin: 0;
  font-weight: bold;
`;

const RestaurantRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #FFD700;
  font-weight: bold;
`;

const RestaurantInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  font-size: 0.9rem;
`;

const RestaurantActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  background: ${props => props.primary ? '#FFD700' : 'transparent'};
  color: ${props => props.primary ? '#000000' : '#FFD700'};
  border: 2px solid #FFD700;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.primary ? '#FFED4E' : '#FFD700'};
    color: ${props => props.primary ? '#000000' : '#000000'};
    transform: translateY(-1px);
  }
`;

const MapSection = styled.section`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
`;

const ResultsCount = styled.p`
  color: #666;
  font-size: 0.9rem;
`;

const SortSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  color: #000000;
  font-size: 0.9rem;
`;

interface Restaurant {
  id: number;
  name: string;
  address: string;
  phone: string;
  openingHours: string;
  rating: number;
  features: string[];
  coordinates: [number, number];
}

const restaurants: Restaurant[] = [
  {
    id: 1,
    name: "Poule & Poulette Brussel Centrum",
    address: "Grote Markt 15, 1000 Brussel",
    phone: "+32 2 123 45 67",
    openingHours: "11:00 - 22:00",
    rating: 4.8,
    features: ["Takeaway", "Reserveren", "Parking"],
    coordinates: [50.8503, 4.3517]
  },
  {
    id: 2,
    name: "Poule & Poulette Antwerpen",
    address: "Meir 45, 2000 Antwerpen",
    phone: "+32 3 123 45 67",
    openingHours: "11:00 - 22:00",
    rating: 4.6,
    features: ["Takeaway", "Reserveren", "Terras"],
    coordinates: [51.2194, 4.4025]
  },
  {
    id: 3,
    name: "Poule & Poulette Gent",
    address: "Korenmarkt 12, 9000 Gent",
    phone: "+32 9 123 45 67",
    openingHours: "11:00 - 22:00",
    rating: 4.7,
    features: ["Takeaway", "Reserveren"],
    coordinates: [51.0543, 3.7174]
  },
  {
    id: 4,
    name: "Poule & Poulette Leuven",
    address: "Oude Markt 8, 3000 Leuven",
    phone: "+32 16 123 45 67",
    openingHours: "11:00 - 22:00",
    rating: 4.5,
    features: ["Takeaway", "Reserveren", "Parking"],
    coordinates: [50.8798, 4.7005]
  },
  {
    id: 5,
    name: "Poule & Poulette Brugge",
    address: "Markt 20, 8000 Brugge",
    phone: "+32 50 123 45 67",
    openingHours: "11:00 - 22:00",
    rating: 4.9,
    features: ["Takeaway", "Reserveren", "Terras"],
    coordinates: [51.2093, 3.2247]
  }
];

const filters = [
  "Alle", "Takeaway", "Reserveren", "Parking", "Terras", "Open Nu"
];

const RestaurantsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('Alle');
  const [sortBy, setSortBy] = useState('rating');

  const handleRoute = (restaurant: Restaurant) => {
    console.log('Route button clicked for:', restaurant.name);
    const { coordinates } = restaurant;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${coordinates[0]},${coordinates[1]}`;
    window.open(url, '_blank');
  };

  const handleCall = (phone: string) => {
    console.log('Call button clicked for:', phone);
    window.open(`tel:${phone}`, '_self');
  };

  const handleReserve = (restaurant: Restaurant) => {
    console.log('Reserveren button clicked for:', restaurant.name);
    // Navigate to reservation page with restaurant info
    navigate('/reserveren', { 
      state: { 
        selectedRestaurant: restaurant.name,
        restaurantAddress: restaurant.address 
      } 
    });
  };

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurant.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = activeFilter === 'Alle' || restaurant.features.includes(activeFilter);
    return matchesSearch && matchesFilter;
  });

  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'distance':
        return 0; // Would implement distance calculation
      default:
        return 0;
    }
  });

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Onze Restaurants</HeroTitle>
        <HeroSubtitle>
          Vind een Poule & Poulette restaurant bij jou in de buurt
        </HeroSubtitle>
      </HeroSection>

      <SearchSection>
        <SearchContainer>
          <SearchInput>
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Zoek op stad, postcode of restaurantnaam..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchInput>
          <FilterButton>
            <Filter size={20} />
            Filters
          </FilterButton>
        </SearchContainer>

        <FilterSection>
          {filters.map((filter) => (
            <FilterChip
              key={filter}
              active={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </FilterChip>
          ))}
        </FilterSection>
      </SearchSection>

      <ContentGrid>
        <RestaurantsList>
          <ResultsInfo>
            <ResultsCount>
              {sortedRestaurants.length} restaurant{sortedRestaurants.length !== 1 ? 'en' : ''} gevonden
            </ResultsCount>
            <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="rating">Sorteren op rating</option>
              <option value="name">Sorteren op naam</option>
              <option value="distance">Sorteren op afstand</option>
            </SortSelect>
          </ResultsInfo>

          {sortedRestaurants.map((restaurant, index) => (
            <RestaurantCard
              key={restaurant.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <RestaurantHeader>
                <RestaurantName>{restaurant.name}</RestaurantName>
                <RestaurantRating>
                  <Star size={16} fill="#FFD700" />
                  {restaurant.rating}
                </RestaurantRating>
              </RestaurantHeader>

              <RestaurantInfo>
                <InfoRow>
                  <MapPin size={16} />
                  {restaurant.address}
                </InfoRow>
                <InfoRow>
                  <Phone size={16} />
                  {restaurant.phone}
                </InfoRow>
                <InfoRow>
                  <Clock size={16} />
                  {restaurant.openingHours}
                </InfoRow>
              </RestaurantInfo>

              <RestaurantActions>
                <ActionButton primary onClick={() => handleRoute(restaurant)}>
                  <Navigation size={16} />
                  Route
                </ActionButton>
                <ActionButton onClick={() => handleCall(restaurant.phone)}>
                  <Phone size={16} />
                  Bellen
                </ActionButton>
                <ActionButton onClick={() => handleReserve(restaurant)}>
                  <Clock size={16} />
                  Reserveren
                </ActionButton>
              </RestaurantActions>
            </RestaurantCard>
          ))}
        </RestaurantsList>

        <MapSection>
          <SectionTitle>
            <MapPin size={24} />
            Kaart
          </SectionTitle>
          <InteractiveMap />
        </MapSection>
      </ContentGrid>
    </PageContainer>
  );
};

export default RestaurantsPage;
